import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaCheck, FaTimes, FaPlaneDeparture, FaPlaneArrival, FaHotel, FaCar } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './SpitiValley.css';

const SpitiValley = () => {
  return (
    <>
      <Helmet>
        <title>Spiti Valley Tour - NomadTrailz</title>
        <meta name="description" content="Experience the mystical Spiti Valley with NomadTrailz. High-altitude desert valley with ancient monasteries and stunning landscapes." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Hero Section */}
        <section className="destination-hero">
          <div className="hero-background">
            <img 
              src="https://res.cloudinary.com/duym2bgp2/image/upload/v1755000420/muw23asrqefdeozseolq.jpg?v=2" 
              alt="Spiti Valley" 
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Spiti Valley
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A high-altitude desert valley known for its stunning landscapes and ancient monasteries
            </motion.p>
          </div>
        </section>

        {/* Tour Details */}
        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div 
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>
                    Spiti Valley, often called "Little Tibet," is a high-altitude desert valley 
                    located in the Indian state of Himachal Pradesh. This mystical land is known 
                    for its ancient monasteries, stunning landscapes, and unique culture that 
                    has remained largely unchanged for centuries.
                  </p>
                </motion.div>

                <motion.div 
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Ancient monasteries and Buddhist culture</li>
                    <li>Stunning high-altitude landscapes</li>
                    <li>Traditional Spitian villages</li>
                    <li>Adventure activities and trekking</li>
                    <li>Local cuisine and hospitality</li>
                    <li>Photography opportunities</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Detailed Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Delhi to Shimla - Transfer to Sangla/Chitkul</h4>
                    <ul>
                      <li>Leave from Delhi between 09:00 to 11:30 pm in the evening</li>
                      <li>Reach Shimla early morning between 8:00 to 9:00 am & transfer to Sangla</li>
                      <li>Visit Kinnaur gate and get some cool photographs</li>
                      <li>Reach Sangla by evening and check into your hotels, freshen up & relax</li>
                      <li>Night Briefing session for the following day</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Chitkul Exploration - Khab - Tabo</h4>
                    <ul>
                      <li>Early morning freshen up & get ready for the day</li>
                      <li>Have breakfast and leave for Chitkul Exploration</li>
                      <li>Leave for Tabo via khab Sangam</li>
                      <li>Reach Tabo & check into your Hotel/Homestay</li>
                      <li>Dinner and night briefing</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Tabo - Dhankar - Kaza</h4>
                    <ul>
                      <li>Early morning freshen up & leave for kaza</li>
                      <li>Visit the Tabo Monastery & Tabo caves which are 1000year old & have meditation session</li>
                      <li>Visit Dhanker Monastery which was a capital of spiti in the past</li>
                      <li>Reach Kaza, check into your homestay, dinner and overnight stay at Kaza</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Hikkim - Komic - Langza</h4>
                    <ul>
                      <li>Early morning freshen up & get ready for the day</li>
                      <li>Head towards visiting the Highest post office in the world & write to your loved ones</li>
                      <li>Next, visit the highest village in the world know as Komic and Langza whic is known to be the fossil village of india</li>
                      <li>Come back to kaza, have dinner & overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 5: Chicham Bridge - Key Monastery</h4>
                    <ul>
                      <li>Wake up Early morning & head towards sightseeing of Spiti Valley</li>
                      <li>Visit the iconic monastery known as - Key Monastery</li>
                      <li>Head towards the highest suspension bridge in asia (Chicham Bridge)</li>
                      <li>Come back to kaza and go for Self exploration in Kaza market</li>
                      <li>Come to homestay dinner and overnight stay at kaza</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 6: Kaza to Kalpa</h4>
                    <ul>
                      <li>Today Morning, pack your bags and leave for Kalpa</li>
                      <li>Enroute we will cover Gue Monastery and Nako Lake</li>
                      <li>Reach kalpa and check into your hotel. Take some rest</li>
                      <li>Dinner and overnight stay</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 7: Shimla to Delhi Transfer</h4>
                    <ul>
                      <li>Wake up early, have breakfast & explore Sucide point & transfer to shimla</li>
                      <li>Reach shimla and depart for delhi via volvo</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div 
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li><FaCheck className="check-icon" />Volvo transfer from Delhi to Shimla both sides</li>
                    <li><FaCheck className="check-icon" />Entire travel from Shimla to Spiti by Tempo Traveler/Cab</li>
                    <li><FaCheck className="check-icon" />6 nights Accommodation</li>
                    <li><FaCheck className="check-icon" />Total of 12 meals (6 Dinner + 6 Breakfast)</li>
                    <li><FaCheck className="check-icon" />All inner line permits for the trip</li>
                    <li><FaCheck className="check-icon" />Driver Night Charges, Toll Tax, Parking Charges</li>
                    <li><FaCheck className="check-icon" />Team Captain throughout the trip</li>
                  </ul>
                </motion.div>

                <motion.div 
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li><FaTimes className="times-icon" />Anything not mentioned in the inclusions</li>
                    <li><FaTimes className="times-icon" />Any cost arising due to natural calamities</li>
                    <li><FaTimes className="times-icon" />Any personal expense like tips, entry fees, camera charges</li>
                    <li><FaTimes className="times-icon" />Any kind of food & beverages not included in the package</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div 
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">₹ 17,000</span>
                    <span className="duration">7 Days / 6 Nights</span>
                  </div>
                  
                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>Spiti Valley, Himachal Pradesh</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: May to October</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: Moderate</span>
                    </div>
                  </div>

                </motion.div>

                <motion.div 
                  className="tour-logistics-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <h3>Tour Logistics</h3>
                  <div className="logistics-grid">
                    <div className="logistics-item">
                      <FaPlaneDeparture className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Pickup Point:</span>
                        <span className="logistics-value">Delhi: ISBT Kashmiri Gate</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaPlaneArrival className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Drop Point:</span>
                        <span className="logistics-value">Delhi: ISBT Kashmiri Gate</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaHotel className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Accommodation:</span>
                        <span className="logistics-value">Manali (1N) - Kaza (3N) - Kalpa (1N) - Shimla (1N)</span>
                      </div>
                    </div>
                    <div className="logistics-item">
                      <FaCar className="logistics-icon" />
                      <div className="logistics-content">
                        <span className="logistics-label">Vehicle:</span>
                        <span className="logistics-value">Tempo Traveller / Innova</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName="Spiti Valley Tour"
                  tourPrice="₹ 17,000"
                  tourDuration="7 Days / 6 Nights"
                />
              </motion.div>
            </div>
                 </div>
               </section>
             </motion.div>
           </>
         );
       };

export default SpitiValley;
